import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { requests } from "../../../common/RestAPI";
import { base64Encoding, generateQueryParam, getTimestampToDate } from "../../../common/util";
import { PAGE_URL, PLATFORM_URL } from "../../../value/Url";
import { ALERTS, POPUP_TYPE } from "../../../value/type";
import ContentsProps from "../../../interfaceTypes/ContentsProps";
import StorageMgmt from "./StorageMgmt";
import StorageData, { actionMenus, tableHeaders, storageAddModalContent, storageUpdateModalContent } from "./StorageData";
import { PopupModalItemType } from "../../base/modal/PopupModal";
import { useNavigate } from "react-router-dom";
import { View } from "react-native";



interface StorageMgmtContainerProps extends ContentsProps {
    moveDetailCb: Function;
}



export default function StorageMgmtContainer(props:StorageMgmtContainerProps) {
    const { t } = useTranslation();
    const [infoText, setInfoText] = useState('');
    const [infoError, setInfoError] = useState(false);

    const fetchIdRef = useRef(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [selectItems, setSelectItems] = useState([]);
    const [tableData, setTableData] = useState<StorageData[]>([]);
    const [queryData, setQueryData] = useState<StorageData[]>([]);
    const [refreshYn, setRefreshYn] = useState(false);

    const [executeDeleteStorageYn, setExecuteDeleteStorageYn] = useState(false);
    const [actionResultTitle, setActionResultTitle] = useState("");
    const [actionResultMessage, setActionResultMessage] = useState("");
    const [actionResults, setActionResults] = useState<PopupModalItemType[]>([]);
    const [actionResultCount, setActionResultCount] = useState(0);

    const headers = useMemo(() => tableHeaders, []);
    const navigate = useNavigate();


    useEffect(() => {
        AlertFunction('');
    }, []);


    function AlertFunction(text:string, error=false) {
        setInfoText(text);
        setInfoError(error);
    }


    useEffect(() => {
        let v: StorageData[] = []

        v = queryData?.map((v, i) => {
            return {
                ...v,
                storage_pool: {
                    storage_pool_total_capacity_gb: v.storage_pool_total_capacity_gb,
                    storage_pool_allocated_capacity_gb: v.storage_pool_allocated_capacity_gb,
                    storage_pool_free_capacity_gb: v.storage_pool_free_capacity_gb,
                    storage_pool_percent: Math.ceil(v.storage_pool_allocated_capacity_gb / v.storage_pool_total_capacity_gb * 100)
                }
            }
        });

        setTableData(v);

    }, [queryData]);


    useEffect(() => {
        if(actionResults.length === 0) {
            return ;
        }
        if(actionResultMessage && actionResultTitle) {
            const items = actionResults.map((item, index) => {
                return { target: item.target, successYn: item.successYn, reason: item.reason}
            });
            const completeYn = items.length === selectItems.length ? true : false;
            props.showPopupModalCb({
                openYn: true,
                type: POPUP_TYPE.LIST_MESSAGE_COMPLETE_POPUP,
                title: actionResultTitle,
                message: actionResultMessage,
                items: items,
                completeTargetText: t("STORAGE_NAME_TEXT"),
                code: "COMPLETE",
                cancelButtonYn: false,
                confirmButtonCb: confirmAction,
                completeYn: completeYn
            });

            if(completeYn) {
                props.setLoading(false);
            }
        }
        if(actionResults.length > 0) {
            setActionResultCount(actionResultCount - 1);
        }
    }, [actionResults, actionResultTitle, actionResultMessage]);


    useEffect(() => {
        if(actionResultCount === selectItems.length) {
            props.setLoading(true);
        }
        if(actionResultCount > 0) {
            if(executeDeleteStorageYn) {
                setActionResultTitle(t("STORAGE_DELETE_COMPLETE_TEXT"));
                setActionResultMessage(t("MESSAGE_STORAGE_DELETE_COMPLETE_MESSAGE"));
                executeDeleteStorage(selectItems[selectItems.length - actionResultCount]);
            }
        }
    }, [actionResultCount, executeDeleteStorageYn]);


    function executeResult(target, successYn, reason:string="") {
        const results = actionResults.concat([{target: target, successYn: successYn, reason:reason }]);
        setActionResults(results);
    }


    function executeDeleteStorage(item) {
        requests.delete(PLATFORM_URL.STORAGE_POOL + "/" + item.hostId)
        .then((response) => {
            executeResult(item.storagePoolName, true);
        })
        .catch((error) => {
            executeResult(item.storagePoolName, false);
        })
    }

    function confirmAction(code) {
        if(code === "STORAGE_DELETE") {
            setActionResultCount(selectItems.length);
            setExecuteDeleteStorageYn(true);
        } else if(code !== "COMPLETE") {
            setActionResultCount(selectItems.length);
        } else {
            setActionResultCount(0);
            setActionResults([]);
            setActionResultMessage("");
            setActionResultTitle("");
            setExecuteDeleteStorageYn(false);
            setRefreshYn(true);
            AlertFunction('');
        }
    }

    function clickActionMenu(code) {

        if(code === 'STORAGE_SYNC') {
            requests.post(PLATFORM_URL.STORAGE_POOL + PLATFORM_URL.SYNC, {})
            .then((response) => {
                if (response.status === 200) {
                    AlertFunction(t("SYNC_COMPLETED_TEXT"));
                    setRefreshYn(true);
                }
            })
        }
    }


    function clickRow(item) {
        props.moveDetailCb(item.storage_pool_id);
        navigate(`${PAGE_URL.DETAIL_MANAGEMENT}`);
    }


    function executeLoadStorageList(pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search, showLoading) {
        if(showLoading) {
            setCurrentPage(pageIndex);
            props.setLoading(true);
        }
        requests.get(PLATFORM_URL.STORAGE_POOL, generateQueryParam(pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search))
        .then((response) => {
            if (response.status === 200 && response.data && pageIndex === response.data.page-1) {
                setQueryData(response.data.items);
                setPageCount(response.data.pages);
                setTotalItemCount(response.data.total);
            }
            if(showLoading) {
                props.setLoading(false);
                setRefreshYn(false);
            }
        })
        .catch((_) => {
            AlertFunction(t("MESSAGE_CANNOT_LOAD_DATA_MESSAGE"), true);
            if(showLoading) {
                props.setLoading(false);
                setRefreshYn(false);
            }
        })
    }


    const fetchData = useCallback(({ pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search, showLoading }) => {
        const fetchId = ++fetchIdRef.current
        if(fetchId === fetchIdRef.current) {
            executeLoadStorageList(pageSize, pageIndex, sortByName, sortByDescYn, categoryType, categoryCode, search, showLoading);
        }
    }, []);


    return (
        <StorageMgmt
            clickActionMenu={clickActionMenu}
            clickRow={clickRow}
            fetchData={fetchData}
            currentPage={currentPage}
            pageCount={pageCount}
            headers={headers}
            tableData={tableData}
            totalItemCount={totalItemCount}
            refreshYn={refreshYn}
            infoText={infoText}
            infoError={infoError}
        />
    )
}